import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveReturnTo } from '../lib/authReturnTo';

/**
 * Inicia el flujo OAuth de Google con Supabase.
 * Si el usuario ya está logueado, redirige a /dashboard.
 */
export default function GoogleLoginButton({ className = '', children, returnTo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClick = async () => {
    if (user) {
      navigate('/dashboard', { replace: true });
      return;
    }
    setError('');
    setLoading(true);
    // Se guarda ANTES del redirect de página completa a Google: el estado de
    // React Router (location.state) no sobrevive ese salto.
    saveReturnTo(returnTo || `${location?.pathname || ''}${location?.search || ''}`);
    const { error: err } = await signInWithGoogle();
    setLoading(false);
    if (err) setError(err?.message || 'Error al iniciar sesión con Google.');
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-border bg-card hover:bg-muted transition-colors duration-200 font-caption font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:pointer-events-none min-h-[44px]"
        aria-label="Iniciar sesión con Google"
      >
        {loading ? (
          <span className="text-sm">Redirigiendo…</span>
        ) : (
          <>
            <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {children || 'Continuar con Google'}
          </>
        )}
      </button>
      {error && (
        <p className="mt-2 text-sm text-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
